import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import {
  AlertTriangle, Clock, MapPin, User, Phone, Check, X,
  ChevronRight, Send, RefreshCw, Shield, Star, Car, Heart,
  Bell, CheckCircle, XCircle, Loader2, ArrowRight, AlertCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// Priority badge component
const PriorityBadge = ({ priority }) => {
  const config = {
    critical: { bg: "bg-red-100 text-red-700 border-red-200", label: "CRITICAL" },
    high: { bg: "bg-orange-100 text-orange-700 border-orange-200", label: "URGENT" },
    medium: { bg: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "MEDIUM" },
    low: { bg: "bg-gray-100 text-gray-600 border-gray-200", label: "LOW" },
  };
  const { bg, label } = config[priority] || config.medium;
  
  return (
    <Badge variant="outline" className={`${bg} text-xs font-bold`}>
      {label}
    </Badge>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    needs_coverage: { bg: "bg-red-500", label: "Needs Coverage" },
    notifications_sent: { bg: "bg-amber-500", label: "Notifications Sent" },
    caregiver_accepted: { bg: "bg-blue-500", label: "Caregiver Accepted" },
    covered: { bg: "bg-emerald-500", label: "Covered" },
    escalated: { bg: "bg-purple-500", label: "Escalated" },
    cancelled: { bg: "bg-gray-500", label: "Cancelled" },
  };
  const { bg, label } = config[status] || config.needs_coverage;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-white text-xs font-medium ${bg}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-white/50" />
      {label}
    </span>
  );
};

// Caregiver recommendation card
const RecommendationCard = ({ caregiver, onAssign, onNotify, isAssigning }) => {
  const getScoreBg = (score) => {
    if (score >= 90) return "bg-emerald-500";
    if (score >= 75) return "bg-emerald-400";
    if (score >= 60) return "bg-blue-500";
    return "bg-amber-500";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300 transition-all">
      <div className="flex items-start justify-between gap-4">
        {/* Left: Avatar + Name */}
        <div className="flex items-start gap-3 flex-1">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
            {caregiver.photo_url ? (
              <img src={caregiver.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <User className="w-6 h-6 text-gray-500" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{caregiver.name}</h3>
              {caregiver.is_preferred && (
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              )}
            </div>
            
            {/* Positive reasons */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {caregiver.positive_reasons?.slice(0, 4).map((reason, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full"
                >
                  <Check className="w-3 h-3" />
                  {reason}
                </span>
              ))}
            </div>
            
            {/* Warnings */}
            {caregiver.warnings?.length > 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1 mt-1.5">
                <AlertTriangle className="w-3 h-3" />
                {caregiver.warnings[0]}
              </p>
            )}
            
            {/* Quick info */}
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
              {caregiver.distance_miles != null && (
                <span className="flex items-center gap-1">
                  <Car className="w-3 h-3" />
                  {caregiver.distance_miles.toFixed(1)} mi
                </span>
              )}
              {caregiver.previous_visits > 0 && (
                <span className="flex items-center gap-1">
                  <Heart className="w-3 h-3" />
                  {caregiver.previous_visits} prev visits
                </span>
              )}
              {caregiver.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {caregiver.phone}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Right: Score + Actions */}
        <div className="flex flex-col items-end gap-2">
          <div className={`px-3 py-1.5 rounded-full text-white font-bold text-lg ${getScoreBg(caregiver.match_score)}`}>
            {Math.round(caregiver.match_score)}%
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNotify(caregiver)}
              className="text-xs"
            >
              <Send className="w-3 h-3 mr-1" />
              Notify
            </Button>
            <Button
              size="sm"
              onClick={() => onAssign(caregiver)}
              disabled={isAssigning}
              className="bg-[#06372B] hover:bg-[#0a4a3a] text-xs"
              data-testid={`assign-btn-${caregiver.caregiver_id}`}
            >
              {isAssigning ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Check className="w-3 h-3 mr-1" />
              )}
              Assign
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Coverage event card
const CoverageEventCard = ({ event, onSelect, isSelected }) => {
  return (
    <button
      onClick={() => onSelect(event)}
      data-testid={`coverage-event-${event.id}`}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        isSelected
          ? "border-red-500 bg-red-50/50"
          : "border-gray-200 hover:border-gray-300 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <PriorityBadge priority={event.priority} />
            <StatusBadge status={event.status} />
          </div>
          
          <h3 className="font-semibold text-gray-900 mt-2">{event.client_name}</h3>
          
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {event.visit_date} at {event.start_time}
            </span>
            <span>{event.duration_hours}h</span>
          </div>
          
          <p className="text-sm text-gray-600 mt-2">
            <span className="font-medium">{event.event_type_display}</span>
            {event.original_caregiver_name && (
              <span className="text-gray-400"> — {event.original_caregiver_name}</span>
            )}
          </p>
          
          {event.reason && (
            <p className="text-xs text-gray-400 mt-1 italic">"{event.reason}"</p>
          )}
        </div>
        
        <div className="text-right text-xs text-gray-400">
          {event.time_since_created}
        </div>
      </div>
    </button>
  );
};

// Main Coverage Center component
export default function CoverageCenter() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  
  // Data state
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  
  // Action state
  const [isAssigning, setIsAssigning] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [notifyCaregiver, setNotifyCaregiver] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState("");
  
  // Create event state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  
  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/coverage/dashboard");
      setDashboard(data);
      
      // Auto-select first event if none selected
      if (!selectedEvent && data.events?.length > 0) {
        setSelectedEvent(data.events[0]);
        setRecommendations(data.events[0].recommendations || []);
      }
    } catch (err) {
      console.error("Failed to load coverage dashboard:", err);
      toast.error("Failed to load coverage data");
    } finally {
      setLoading(false);
    }
  }, [selectedEvent]);
  
  useEffect(() => {
    loadDashboard();
    
    // Load event types for create dialog
    api.get("/coverage/event-types").then(({ data }) => {
      setEventTypes(data.event_types);
    }).catch(console.error);
    
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, [loadDashboard]);
  
  // Load specific event if URL has eventId
  useEffect(() => {
    if (eventId) {
      api.get(`/coverage/events/${eventId}`).then(({ data }) => {
        setSelectedEvent(data.event);
        setRecommendations(data.recommendations || []);
      }).catch(err => {
        console.error("Failed to load event:", err);
        toast.error("Coverage event not found");
      });
    }
  }, [eventId]);
  
  // Handle event selection
  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    setRecommendations(event.recommendations || []);
    
    // Load fresh recommendations if not included
    if (!event.recommendations) {
      setLoadingRecs(true);
      try {
        const { data } = await api.get(`/coverage/events/${event.id}`);
        setRecommendations(data.recommendations || []);
      } catch (err) {
        console.error("Failed to load recommendations:", err);
      } finally {
        setLoadingRecs(false);
      }
    }
  };
  
  // Handle assign caregiver
  const handleAssign = async (caregiver) => {
    if (!selectedEvent) return;
    
    setIsAssigning(true);
    try {
      const { data } = await api.post(`/coverage/events/${selectedEvent.id}/assign`, {
        caregiver_id: caregiver.caregiver_id,
        notify_client: true,
      });
      
      toast.success(`Coverage assigned to ${caregiver.name}!`);
      
      // Refresh dashboard
      loadDashboard();
      setSelectedEvent(null);
      setRecommendations([]);
      
    } catch (err) {
      console.error("Failed to assign coverage:", err);
      toast.error(err.response?.data?.detail || "Failed to assign coverage");
    } finally {
      setIsAssigning(false);
    }
  };
  
  // Handle notify caregiver
  const handleNotify = (caregiver) => {
    setNotifyCaregiver(caregiver);
    setNotifyMessage("");
    setShowNotifyDialog(true);
  };
  
  const sendNotification = async () => {
    if (!selectedEvent || !notifyCaregiver) return;
    
    try {
      await api.post(`/coverage/events/${selectedEvent.id}/notify`, {
        caregiver_id: notifyCaregiver.caregiver_id,
        message: notifyMessage || undefined,
      });
      
      toast.success(`Notification sent to ${notifyCaregiver.name}`);
      setShowNotifyDialog(false);
      loadDashboard();
      
    } catch (err) {
      console.error("Failed to send notification:", err);
      toast.error("Failed to send notification");
    }
  };
  
  // Health status indicator
  const getHealthIndicator = () => {
    if (!dashboard?.health) return null;
    
    const { health_status, critical_count, total_active } = dashboard.health;
    
    if (health_status === "healthy") {
      return (
        <div className="flex items-center gap-2 text-emerald-600">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">All Visits Covered</span>
        </div>
      );
    }
    
    if (health_status === "critical") {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 animate-pulse" />
          <span className="font-medium">{critical_count} CRITICAL Coverage Needed</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2 text-amber-600">
        <AlertTriangle className="w-5 h-5" />
        <span className="font-medium">{total_active} Coverage Needed</span>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#06372B]" />
              <h1 className="text-xl font-bold text-gray-900">Coverage Center</h1>
            </div>
            
            {/* Health indicator */}
            <div className="ml-4">
              {getHealthIndicator()}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={loadDashboard}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex h-[calc(100vh-73px)]">
        {/* Left: Event list */}
        <div className="w-96 border-r border-gray-200 bg-white overflow-y-auto">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Active Coverage Needs</h2>
            <p className="text-sm text-gray-500 mt-1">
              {dashboard?.total_active || 0} visits need coverage
            </p>
          </div>
          
          <div className="p-3 space-y-3">
            {dashboard?.events?.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                <p className="font-medium">All visits are covered!</p>
                <p className="text-sm mt-1">No coverage needs at this time.</p>
              </div>
            ) : (
              dashboard?.events?.map((event) => (
                <CoverageEventCard
                  key={event.id}
                  event={event}
                  isSelected={selectedEvent?.id === event.id}
                  onSelect={handleSelectEvent}
                />
              ))
            )}
          </div>
        </div>
        
        {/* Right: Selected event details + recommendations */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <AnimatePresence mode="wait">
            {selectedEvent ? (
              <motion.div
                key={selectedEvent.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Event header */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <PriorityBadge priority={selectedEvent.priority} />
                        <StatusBadge status={selectedEvent.status} />
                      </div>
                      
                      <h2 className="text-2xl font-bold text-gray-900">{selectedEvent.client_name}</h2>
                      
                      <div className="flex items-center gap-4 mt-2 text-gray-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {selectedEvent.visit_date} at {selectedEvent.start_time} - {selectedEvent.end_time}
                        </span>
                        <span>{selectedEvent.duration_hours}h</span>
                      </div>
                      
                      {selectedEvent.client_address && (
                        <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {selectedEvent.client_address}
                        </p>
                      )}
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-700">{selectedEvent.event_type_display}</p>
                      {selectedEvent.original_caregiver_name && (
                        <p className="text-sm text-gray-500 mt-1">
                          Originally: {selectedEvent.original_caregiver_name}
                        </p>
                      )}
                      {selectedEvent.reason && (
                        <p className="text-sm text-gray-400 mt-1 italic">"{selectedEvent.reason}"</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Recommendations */}
                {selectedEvent.status !== "covered" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Best Available Replacements</h3>
                        <p className="text-sm text-gray-500">Ranked by Business Rules — scores are explainable and auditable</p>
                      </div>
                      {loadingRecs && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
                    </div>
                    
                    <div className="space-y-3">
                      {recommendations.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
                          <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="font-medium">No available caregivers found</p>
                          <p className="text-sm mt-1">All caregivers are either scheduled or unavailable</p>
                        </div>
                      ) : (
                        recommendations.map((caregiver) => (
                          <RecommendationCard
                            key={caregiver.caregiver_id}
                            caregiver={caregiver}
                            onAssign={handleAssign}
                            onNotify={handleNotify}
                            isAssigning={isAssigning}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}
                
                {/* Covered state */}
                {selectedEvent.status === "covered" && (
                  <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-8 text-center">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                    <h3 className="text-xl font-bold text-emerald-800">Coverage Complete</h3>
                    <p className="text-emerald-600 mt-2">
                      Covered by {selectedEvent.replacement_caregiver_name}
                    </p>
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">Select a coverage event</p>
                  <p className="text-sm mt-1">Click on an event to see recommendations</p>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </main>
      
      {/* Notify Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notify {notifyCaregiver?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-4">
              Send a coverage request notification for {selectedEvent?.client_name} on {selectedEvent?.visit_date}.
            </p>
            
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Message (optional)
            </label>
            <Input
              placeholder="e.g., This is urgent, please respond ASAP"
              value={notifyMessage}
              onChange={(e) => setNotifyMessage(e.target.value)}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotifyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={sendNotification} className="bg-[#06372B]">
              <Send className="w-4 h-4 mr-2" />
              Send Notification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
